import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { DialogTitle } from '@headlessui/react';
import { Check, ChevronDown, ChevronRight, Play, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { StatTile } from '../../components/ui/Card';
import { PageHeader, PageShell, SectionHeading } from '../../components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import {
  useAutomations,
  useAutomationHistory,
  useAutomationsProgress,
  useTriggerAutomation,
  useStopAutomation,
} from '../../hooks/useAutomations';
import type {
  AutomationStatus,
  AutomationHistoryRun,
  AutomationProgressEntry,
  AutomationProgressStep,
} from '../../api/automations';
import { HISTORY_LIMIT } from '../../api/automations';
import { MISSED_RUN_LABEL, failureCause, isConfigGap, isMissedRunError, partialFailure, restartCasualtyKind, stripDevPaths } from '../Integrations/syncError';
import { formatDayMonth, formatDayMonthYearTime } from '../../utils/dateFormat';
import { formatNextRun, nextRun, scheduleLabel, scheduledSlot, slotOfRun } from './schedule';
import { runOutputSummary, attentionCounts, cancelledAfterLastRun, displayStatus, failedLabel, isManualNeverRan, isStaleFailure, slotMiss, slotMissWithHistory, type DisplayStatus } from './automationStatus';
import {
  firesOncePerDay,
  groupRepeatedFailures,
  isStuckRun,
  nextRunNote,
  noRunNote,
  oneRowPerSlot,
  repeatDates,
  runShownAt,
  sortByShownTime,
} from './runGroups';

export function Automations() {
  return (
    <PageShell>
      <PageHeader eyebrow="Admin"
        title="Automations"
        subtitle="Background scripts and scheduled jobs across the OS."
        meta={<span>Times are Brussels time</span>}
        info="Failed jobs are listed first. Click a row for its run history; the play button runs a job now. Jobs run on the API unless another service is named."
      />
      <ScriptsTab />
    </PageShell>
  );
}

/**
 * Fields newer APIs send on the list (routes/automations.ts): `missedAt`, a run
 * the OS lost to a restart after the last run that counts, and `deployedAt`,
 * when the running build went live (null when the deploy does not say).
 */
type AutomationRowData = AutomationStatus;

const brusselsDayKey = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels' });
const brusselsHm = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const brusselsWd = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Brussels', weekday: 'short' });

/** "06:30", "tomorrow 05:15", "Thu 05:15", "Mon 19 Oct 06:30": always with the time. */
function nextRunLabel(at: Date, now: Date): string {
  const short = formatNextRun(at, now);
  return /\d:\d\d/.test(short) ? short : `${brusselsWd.format(at)} ${formatDayMonth(at)} ${brusselsHm.format(at)}`;
}

function formatRunDate(dateStr: string): string {
  return formatDayMonthYearTime(dateStr);
}

/* ─── Scripts Tab ─── */

const CATEGORY_META: Record<string, { label: string; order: number }> = {
  sync: { label: 'Data synchronisation', order: 0 },
  feedback: { label: 'Customer feedback and analysis', order: 1 },
  product: { label: 'Product', order: 2 },
  productivity: { label: 'Team productivity', order: 3 },
  marketing: { label: 'Marketing and lead nurturing', order: 4 },
  reports: { label: 'Reports', order: 5 },
  backup: { label: 'Backups', order: 6 },
};

const SERVICE_LABELS: Record<string, string> = {
  api: 'API',
  'fillout-attio-webhook': 'Fillout webhook',
  'demo-followup': 'Demo follow-up',
};


/**
 * Healthy is the default and shows no dot; only exceptions get one. Running is
 * ink, not indigo: indigo is reserved for links, focus and selection.
 */
const STATUS_META: Record<DisplayStatus, { dot: string; label: string }> = {
  healthy: { dot: '', label: 'Healthy' },
  failed: { dot: 'bg-error', label: 'Failed' },
  running: { dot: 'bg-text-primary animate-pulse', label: 'Running' },
  stuck: { dot: 'bg-warning', label: 'Stuck' },
  unknown: { dot: 'border border-border-dark', label: 'Never ran' },
  skipped: { dot: 'bg-text-muted', label: 'Skipped' },
  paused: { dot: 'bg-text-muted', label: 'Paused' },
  idle: { dot: 'bg-text-muted', label: 'Idle: not connected' },
  missed: { dot: 'bg-warning', label: 'Missed its slot' },
};

type SlotMiss = { at: Date; reason: 'no_run' | 'cancelled' | 'restart' | null };

/** "today's 08:15" or "the 12 Oct 08:15". */
function slotWhen(at: Date, now = new Date()): string {
  const sameDay = brusselsDayKey.format(at) === brusselsDayKey.format(now);
  return sameDay ? `today's ${brusselsHm.format(at)}` : `the ${formatDayMonth(at)} ${brusselsHm.format(at)}`;
}

/**
 * One line for a slot nothing finished in. A run that started and was
 * cancelled is not "missed": the line says what happened to it.
 */
function slotMissLine(miss: SlotMiss, now = new Date()): string {
  const when = slotWhen(miss.at, now);
  if (miss.reason === 'cancelled') return `${capitalize(when)} run was cancelled`;
  if (miss.reason === 'restart') return `${capitalize(when)} run was lost to an OS restart`;
  if (miss.reason === 'no_run') return `Missed ${when} run: no run started`;
  // Older API: the reason is unknown, so say only what is certain.
  return `No completed run in ${when} slot`;
}

/** The dialog's second sentence: why this is amber and not a failure. */
function slotMissNote(miss: SlotMiss): string {
  if (miss.reason === 'cancelled') return 'Not a failure: the run was stopped before it finished (Stop, Clear or the API).';
  if (miss.reason === 'restart') return 'Not a failure: the job queue lost the run when the OS restarted.';
  if (miss.reason === 'no_run') return 'Not a failure: nothing ran in that slot.';
  return 'Not a failure: no run finished in that slot.';
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Where to act on a failing job, when the OS has a page for it. A setting
 * that lives in code says so instead of pretending there is a switch.
 */
const ACT_LINKS: Record<string, { label: string; href: string; note?: string }> = {
  'gtm-tender-sourcing-weekly': { label: 'Open GTM › Sourcing', href: '/gtm/sourcing' },
  'gtm-hiring-sourcing-weekly': { label: 'Open GTM › Sourcing', href: '/gtm/sourcing' },
  'deal-closed-won-handover': {
    label: 'Open Integrations › Attio',
    href: '/integrations',
    note: 'It runs on an Attio webhook that arrives without a deal id.',
  },
};

/** Remembered open categories; healthy ones start folded to their header line. */
function useOpenCategories(): [Set<string>, (cat: string) => void] {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (cat: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  return [open, toggle];
}

function ScriptsTab() {
  const [selectedAutomation, setSelectedAutomation] = useState<AutomationStatus | null>(null);
  const [runFeedback, setRunFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const { data: automationsData, isLoading: automationsLoading, error: automationsError, refetch } = useAutomations();
  const hasRunningAutomations = automationsData?.automations.some((a) => a.status === 'running' || a.status === 'stuck') ?? false;
  const { data: progressData } = useAutomationsProgress(hasRunningAutomations);
  const { mutate: triggerAutomationMutation, isPending: isTriggerPending, variables: triggeringAutomationId } = useTriggerAutomation();
  const { mutate: stopAutomationMutation, isPending: isStopPending, variables: stoppingAutomationId } = useStopAutomation();
  const [openCategories, toggleCategory] = useOpenCategories();

  if (automationsLoading) return <ScriptsSkeleton />;
  if (automationsError)
    return <ErrorState title="Couldn’t load automations" error={automationsError} onRetry={() => refetch()} />;

  const automations: AutomationRowData[] = automationsData?.automations ?? [];
  const extras = automationsData as { deployedAt?: string | null; warning?: string | null } | undefined;
  const deployedAt = extras?.deployedAt ?? null;
  const total = automations.length;
  // Every job lands in exactly one bucket, so the tiles add up to the total.
  const buckets = automations.reduce<Record<DisplayStatus, number>>(
    (acc, a) => ({ ...acc, [displayStatus(a)]: acc[displayStatus(a)] + 1 }),
    { healthy: 0, failed: 0, running: 0, stuck: 0, unknown: 0, skipped: 0, paused: 0, idle: 0, missed: 0 }
  );
  const attention = attentionCounts(automations);
  const idle = buckets.paused + buckets.unknown + buckets.skipped + buckets.idle;
  const idleParts = [
    buckets.idle > 0 && `${buckets.idle} not connected`,
    buckets.paused > 0 && `${buckets.paused} paused`,
    buckets.unknown > 0 && `${buckets.unknown} never ran`,
    buckets.skipped > 0 && `${buckets.skipped} skipped`,
  ].filter(Boolean).join(' · ');

  if (automations.length === 0) return <EmptyState title="No automations registered" />;

  // Failed jobs get their own group at the top; the categories below hold the rest.
  // Stale failures (nothing has retried them in 30+ days) lead: they will not
  // fix themselves and need someone to decide whether to fix or retire them.
  // Stuck runs (unfinished for over an hour) sit with them: nothing will finish them.
  const failed = automations
    .filter((a) => a.status === 'failed' || a.status === 'stuck' || displayStatus(a) === 'missed')
    .sort((a, b) => Number(isStaleFailure(b)) - Number(isStaleFailure(a)));
  const grouped = automations
    .filter((a) => a.status !== 'failed' && a.status !== 'stuck' && displayStatus(a) !== 'missed')
    .reduce<Record<string, AutomationStatus[]>>((acc, automation) => {
      (acc[automation.category] ??= []).push(automation);
      return acc;
    }, {});

  const sortedCategories = Object.keys(grouped).sort(
    (a, b) => (CATEGORY_META[a]?.order ?? 99) - (CATEGORY_META[b]?.order ?? 99)
  );

  const renderRow = (automation: AutomationStatus, showCategory = false) => (
    <AutomationRow
      key={automation.id}
      automation={automation}
      showFailure={showCategory}
      deployedAt={deployedAt}
      categoryLabel={showCategory ? CATEGORY_META[automation.category]?.label ?? automation.category : undefined}
      progress={progressData?.progress[automation.id]}
      onClick={() => setSelectedAutomation(automation)}
      onTrigger={() => {
        setRunFeedback(null);
        triggerAutomationMutation(automation.id, {
          onSuccess: (data) => {
            if (data.alreadyRunning) {
              setRunFeedback({ type: 'info', message: data.message || 'A sync is already running.' });
            } else {
              setRunFeedback({ type: 'success', message: data.message || `${automation.name} triggered successfully.` });
            }
          },
          onError: (err) => setRunFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to trigger automation.' }),
        });
      }}
      onStop={() => {
        setRunFeedback(null);
        stopAutomationMutation(automation.id, {
          onSuccess: () => setRunFeedback({ type: 'success', message: `${automation.name} stopped.` }),
          onError: (err) => setRunFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to stop automation.' }),
        });
      }}
      isTriggerPending={isTriggerPending && triggeringAutomationId === automation.id}
      isStopPending={isStopPending && stoppingAutomationId === automation.id}
    />
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatTile label="Healthy" value={`${buckets.healthy} / ${total}`} />
        <StatTile
          label="Needs attention"
          value={attention.total}
          sub={[failedLabel(automations), attention.stuck > 0 ? `${attention.stuck} stuck` : null, attention.missed > 0 ? `${attention.missed} missed` : null, attention.cancelled > 0 ? `${attention.cancelled} cancelled` : null].filter(Boolean).join(' · ') || undefined}
          tooltip="Failed = the last run failed; stale = a failure nothing has retried for 30+ days. Stuck = a run started more than an hour ago and never finished, the same rule as Integrations; Clear marks it cancelled. Missed = no run finished in its latest scheduled slot (lost to a restart or never started); Cancelled = a run started in the slot and was stopped. Both amber, not failures. Home's Needs attention line counts the same jobs."
        />
        <StatTile label="Running" value={buckets.running} />
        <StatTile
          label="Idle"
          value={idle}
          sub={idleParts || undefined}
          tooltip="Not connected = it runs on schedule but its integration is not connected, so it does nothing. Paused = no run for longer than the schedule allows. Never ran = no run recorded. Skipped = the last run chose not to act."
        />
      </div>

      {extras?.warning && <Alert variant="warning">{extras.warning}</Alert>}

      {runFeedback && (
        <Alert
          variant={runFeedback.type === 'success' ? 'success' : runFeedback.type === 'info' ? 'info' : 'error'}
          onDismiss={() => setRunFeedback(null)}
        >
          {runFeedback.message}
        </Alert>
      )}

      {failed.length > 0 && (
        <div className="space-y-2">
          <GroupHeading label="Needs attention" jobs={failed} />
          <div className="divide-y divide-border-light rounded-2xl border border-error/30 bg-surface-primary">
            {failed.map((a) => renderRow(a, true))}
          </div>
        </div>
      )}

      {sortedCategories.map((cat) => {
        // A category with nothing to look at folds to its header line; click to open.
        // A manual job that has never run, a paused or a not-connected one is not something to look at.
        const quiet = grouped[cat]!.every((a) => a.status === 'healthy' || a.status === 'skipped' || isManualNeverRan(a) || ['paused', 'idle'].includes(displayStatus(a)));
        const open = !quiet || openCategories.has(cat);
        return (
          <div key={cat} className="space-y-2">
            <GroupHeading
              label={CATEGORY_META[cat]?.label ?? cat}
              jobs={grouped[cat]!}
              inAttention={failed.filter((a) => a.category === cat).length}
              collapsible={quiet}
              open={open}
              onToggle={() => toggleCategory(cat)}
            />
            {open && (
              <div className="divide-y divide-border-light rounded-2xl border border-border-light bg-surface-primary">
                {grouped[cat]!.map((a) => renderRow(a))}
              </div>
            )}
          </div>
        );
      })}

      {selectedAutomation && (
        <AutomationDetailModal
          automation={automations.find((a) => a.id === selectedAutomation.id) ?? selectedAutomation}
          onClose={() => setSelectedAutomation(null)}
          deployedAt={deployedAt}
        />
      )}
    </div>
  );
}

/** The page's own shape while it loads (four tiles, a heading, a list), so nothing jumps when data lands. */
function ScriptsSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading automations">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-[124px] animate-pulse rounded-2xl border border-border-light bg-surface-primary p-6">
            <div className="h-3 w-20 rounded-sm bg-surface-tertiary" />
            <div className="mt-4 h-7 w-16 rounded-sm bg-surface-tertiary" />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <div className="h-4 w-64 animate-pulse rounded-sm bg-surface-tertiary" />
        <div className="divide-y divide-border-light rounded-2xl border border-border-light bg-surface-primary">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex h-[58px] items-center gap-4 px-4">
              <div className="h-2.5 w-2.5 rounded-full bg-surface-tertiary" />
              <div className="h-3.5 w-72 animate-pulse rounded-sm bg-surface-tertiary" />
              <div className="ml-auto h-3 w-28 animate-pulse rounded-sm bg-surface-tertiary" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Group heading ─── */

/** The eyebrow plus a one-line summary: "12 jobs · 1 paused · next run 06:30". */
function GroupHeading({
  label,
  jobs,
  inAttention = 0,
  collapsible = false,
  open = true,
  onToggle,
}: {
  label: string;
  jobs: AutomationStatus[];
  /** This category's jobs that moved up to Needs attention, so "all healthy" never hides them. */
  inAttention?: number;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  const now = new Date();
  const slotCounts = attentionCounts(jobs, now.getTime());
  const counts = jobs.reduce<Partial<Record<DisplayStatus, number>>>((acc, a) => {
    const st = displayStatus(a, now.getTime());
    acc[st] = (acc[st] ?? 0) + 1;
    return acc;
  }, {});
  const next = jobs
    .filter((a) => displayStatus(a, now.getTime()) !== 'paused')
    .map((a) => nextRun(a.schedule, a.scheduleHuman, now))
    .filter((d): d is Date => d !== null)
    .sort((a, b) => a.getTime() - b.getTime())[0];
  const parts = [
    `${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'}`,
    failedLabel(jobs, now.getTime()),
    counts.stuck ? `${counts.stuck} stuck` : null,
    slotCounts.missed ? `${slotCounts.missed} missed` : null,
    slotCounts.cancelled ? `${slotCounts.cancelled} cancelled` : null,
    counts.running ? `${counts.running} running` : null,
    counts.paused ? `${counts.paused} paused` : null,
    counts.idle ? `${counts.idle} not connected` : null,
    counts.unknown ? `${counts.unknown} never ran` : null,
    next ? `next run ${formatNextRun(next, now)}` : null,
  ].filter(Boolean);
  const attention = inAttention > 0 ? (
    <span className="text-error-dark"> · +{inAttention} in Needs attention</span>
  ) : null;
  if (collapsible) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="group flex w-full flex-wrap items-baseline gap-x-3 gap-y-1 text-left"
      >
        <span className="flex items-center gap-1">
          <SectionHeading>{label}</SectionHeading>
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 self-center text-text-muted group-hover:text-text-primary" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 self-center text-text-muted group-hover:text-text-primary" aria-hidden />
          )}
        </span>
        <span className="text-body-xs text-text-tertiary">
          {parts.join(' · ')}
          {/* "All healthy" never sits next to a job that moved up to Needs attention */}
          {!open && inAttention === 0 && !counts.unknown && !counts.paused && !counts.idle && ' · all healthy'}
          {attention}
        </span>
      </button>
    );
  }
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <SectionHeading>{label}</SectionHeading>
      <span className="text-body-xs text-text-tertiary">
        {parts.join(' · ')}
        {attention}
      </span>
    </div>
  );
}

/* ─── Automation Row ─── */

function AutomationRow({
  automation,
  showFailure = false,
  deployedAt = null,
  categoryLabel,
  progress,
  onClick,
  onTrigger,
  onStop,
  isTriggerPending,
  isStopPending,
}: {
  automation: AutomationRowData;
  /** In Needs attention: the one-line error and "failing since" under the name. */
  showFailure?: boolean;
  /** When the running build went live, for "Fix may be live". */
  deployedAt?: string | null;
  /** Shown in the Needs attention group, where rows from every category mix. */
  categoryLabel?: string;
  progress?: AutomationProgressEntry;
  onClick?: () => void;
  onTrigger: () => void;
  onStop: () => void;
  isTriggerPending: boolean;
  isStopPending: boolean;
}) {
  const status = displayStatus(automation);
  // An API from before `missedSlot` / `lastCancelledAt`: the run history says what happened in the slot.
  const needsHistory = status === 'missed' && automation.missedSlot === undefined;
  const { data: slotHistory } = useAutomationHistory(needsHistory ? automation.id : null);
  const slotRuns = slotHistory?.runs;
  // A failure that left most of the work done (one tender source down) is amber, like its cause.
  const partial = usePartialFailure(automation, !!showFailure && automation.status === 'failed');
  const s = partial ? { dot: 'bg-warning', label: 'Partly failed' } : STATUS_META[status];
  const steps = progress?.steps;
  const serviceLabel = automation.service === 'api' ? null : SERVICE_LABELS[automation.service] ?? automation.service;
  const hasSteps = automation.status === 'running' && steps && steps.length > 0;
  const isRunning = automation.status === 'running';
  const isStuck = automation.status === 'stuck';
  const runningSince = automation.runningSince ?? progress?.startedAt ?? null;
  // A run the OS lost to a restart: amber, never a failure.
  const missedAt = automation.missedAt ?? null;
  // A run cancelled after the last finished one is the latest thing that happened to the job.
  const lastCancelled = automation.lastCancelledAt ?? (needsHistory ? cancelledAfterLastRun(slotRuns, automation.lastRunAt) : null);
  const rowMiss = slotMissWithHistory(slotMiss(automation), slotRuns, automation);
  const missedNext = missedAt || status === 'missed' ? nextRun(automation.schedule, automation.scheduleHuman, new Date()) : null;

  return (
    <div className="group cursor-pointer transition-colors hover:bg-surface-secondary" onClick={onClick}>
      <div className="flex items-center gap-4 px-4 py-2.5">
        {/* Status only for exceptions; healthy rows keep the slot so names line up */}
        {status === 'healthy' && missedAt ? (
          <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-warning" title={MISSED_RUN_LABEL} aria-label={MISSED_RUN_LABEL} />
        ) : status === 'healthy' ? (
          <span className="h-2.5 w-2.5 shrink-0" aria-hidden />
        ) : (
          <span className={clsx('h-2.5 w-2.5 shrink-0 rounded-full', s.dot)} title={s.label} aria-label={s.label} />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2">
            <span className="text-body-sm font-medium text-text-primary">{automation.name}</span>
            {(categoryLabel ?? serviceLabel) && (
              <span className="text-body-xs text-text-muted">{categoryLabel ?? serviceLabel}</span>
            )}
            {status === 'paused' && <Badge>Paused</Badge>}
            {status === 'idle' && (
              <span className="text-body-xs text-text-tertiary" title={automation.idleReason ?? undefined}>
                Idle: not connected
              </span>
            )}
            {isStaleFailure(automation) && (
              <span
                className="text-body-xs font-medium text-error-dark"
                title="Failed, and has not run for more than 30 days: nothing has retried it since. Fix the trigger or retire the automation."
              >
                Stale failure · last ran {formatDayMonth(automation.lastRunAt)}
              </span>
            )}
            {isRunning && (
              <span className="text-body-xs text-text-tertiary">
                {runningSince ? `Running since ${formatRunDate(runningSince)}` : 'Running'}
              </span>
            )}
            {isStuck && (
              <span
                className="text-body-xs font-medium text-warning-dark"
                title="A run started more than an hour ago and never finished. Later runs may have gone through; this leftover keeps the job showing as running. Clear marks it cancelled."
              >
                Stuck · started {runningSince ? formatRunDate(runningSince) : 'over an hour ago'}, never finished
              </span>
            )}
          </div>
          {isRunning && progress?.message && (
            <p className="mt-0.5 line-clamp-1 text-body-xs text-text-tertiary">{progress.message}</p>
          )}
          {status === 'missed' && rowMiss && (
            <p className="mt-0.5 text-body-xs text-warning-dark" title="No run finished in its latest scheduled slot. Not a failure: the job itself did not fail.">
              {slotMissLine(rowMiss)}
              {missedNext && <span className="text-text-tertiary"> · next run {nextRunLabel(missedNext, new Date())}</span>}
            </p>
          )}
          {missedAt && status !== 'missed' && (
            <p
              className="mt-0.5 text-body-xs text-warning-dark"
              title="The job queue lost this run when the OS restarted. It is not a failure of the job or its integration."
            >
              Last run {formatRunDate(missedAt)} missed: OS restarted
              {missedNext && <span className="text-text-tertiary"> · next run {nextRunLabel(missedNext, new Date())}</span>}
            </p>
          )}
          {showFailure && automation.status === 'failed' && <FailureLine automation={automation} deployedAt={deployedAt} />}
        </div>

        <div className="hidden shrink-0 text-right md:block">
          <p className="text-body-xs text-text-secondary">{scheduleLabel(automation.scheduleHuman)}</p>
          <p className="mt-0.5 text-body-xs text-text-muted">
            {/* While a run is going, "Last" is the previous finished run, said as such. A job that
                only polls and skips (not connected) has nothing to say about a last run. */}
            {status === 'idle'
              ? 'Polls skipped: nothing to check'
              : lastCancelled
              ? `Last: ${formatRunDate(lastCancelled)} · Cancelled`
              : automation.lastRunAt
              ? `${isRunning || isStuck ? 'Last finished' : 'Last'}: ${formatRunDate(automation.lastRunAt)}${automation.lastRunManual ? ' · manual' : ''}`
              : 'Never ran'}
          </p>
        </div>

        {/* Run / Stop — a quiet icon button, not a loud pill on every row */}
        <div className="flex h-8 min-w-8 shrink-0 items-center justify-center" onClick={(e) => e.stopPropagation()}>
          {automation.triggerable &&
            (isTriggerPending || isStopPending ? (
              <Spinner size="sm" className="text-text-tertiary" />
            ) : (
              isRunning || isStuck ? (
                // A word, not a hollow square icon, which read as an empty checkbox
                <button
                  type="button"
                  onClick={onStop}
                  title={isStuck ? 'Mark the dead run cancelled so the job stops showing as stuck' : 'Stop this run'}
                  aria-label={`${isStuck ? 'Clear' : 'Stop'} ${automation.name}`}
                  className="rounded-sm px-1.5 py-0.5 text-body-xs font-medium text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary"
                >
                  {isStuck ? 'Clear' : 'Stop'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onTrigger}
                  title="Run now"
                  aria-label={`Run ${automation.name} now`}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-text-tertiary opacity-0 transition-opacity hover:bg-surface-tertiary hover:text-text-primary focus:opacity-100 group-hover:opacity-100"
                >
                  <Play className="h-4 w-4" />
                </button>
              )
            ))}
        </div>

        <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
      </div>

      {hasSteps && (
        <div className="px-4 pb-3 pl-11">
          <div className="grid gap-1">
            {steps.map((step) => (
              <ProgressStepRow key={step.id} step={step} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressStepRow({ step }: { step: AutomationProgressStep }) {
  const iconByStatus: Record<AutomationProgressStep['status'], React.ReactNode> = {
    pending: <div className="h-1.5 w-1.5 rounded-full bg-text-muted" />,
    running: <div className="h-1.5 w-1.5 rounded-full bg-text-primary animate-pulse" />,
    done: <Check className="h-3 w-3 text-success" />,
    failed: <X className="h-3 w-3 text-error" />,
  };

  const textColor: Record<AutomationProgressStep['status'], string> = {
    pending: 'text-text-muted',
    running: 'text-text-secondary',
    done: 'text-text-secondary',
    failed: 'text-error',
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex h-3 w-3 items-center justify-center shrink-0">
        {iconByStatus[step.status]}
      </div>
      <span className={`text-body-xs ${textColor[step.status]}`}>
        {step.label}
      </span>
      {step.detail && (
        <span className="text-body-xs text-text-muted">
          — {step.detail}
        </span>
      )}
    </div>
  );
}

/**
 * The latest failure's headline and how long the job has been failing, read
 * from its recent run history (the list endpoint carries no error text).
 */
function FailureLine({ automation, deployedAt }: { automation: AutomationStatus; deployedAt: string | null }) {
  // The same query the modal reads, so the row and the modal state one streak.
  const { data, isLoading, error } = useAutomationHistory(automation.id);
  if (isLoading) return <p className="mt-0.5 h-4 w-64 max-w-full rounded-sm bg-surface-tertiary/60" aria-hidden />;
  if (error) return <p className="mt-0.5 text-body-xs text-text-tertiary">Error details unavailable</p>;
  const runs = orderedRuns(data?.runs ?? [], automation);
  const latest = runs.find(isFailedRun);
  if (!latest) return null;
  const streak = failingStreak(runs);
  const now = new Date();
  const next = nextRun(automation.schedule, automation.scheduleHuman, now);
  const partial = partialFailure(failedRunText(latest));
  const act = ACT_LINKS[automation.id];
  const stale = isStaleFailure(automation);
  return (
    <>
      {/* The cause wraps, never truncates: "Access denie…" hid the point */}
      <p className="mt-0.5 break-words text-body-xs [overflow-wrap:anywhere]" title={failedRunText(latest)}>
        <span className={partial ? 'text-warning-dark' : 'text-error-dark'}>
          {partial ? partial.cause : failureCause(failureHeadline(latest))}
        </span>
        {partial && <span className="text-text-tertiary"> · {partial.rest}</span>}
        {streak && <span className="text-text-tertiary"> · {streakLabel(streak)}</span>}
      </p>
      {stale ? (
        <p className="mt-0.5 text-body-xs text-text-secondary">
          Needs a decision: fix the cause and use the play button on its row to confirm, or retire it (a code change: the OS has no pause switch).
        </p>
      ) : (
        next && (
          <p className="mt-0.5 text-body-xs text-text-secondary">
            {nextRunNote(
              latest.completedOn ?? latest.startedOn ?? latest.createdOn,
              isConfigGap(failedRunText(latest)),
              deployedAt,
              nextRunLabel(next, now),
              streak?.count ?? 1,
            )}
          </p>
        )
      )}
      {act && <ActLink act={act} />}
    </>
  );
}

/** "Open GTM › Sourcing", with what the fix is when it is not a switch in the OS. */
function ActLink({ act }: { act: { label: string; href: string; note?: string } }) {
  return (
    <p className="mt-0.5 text-body-xs text-text-tertiary" onClick={(e) => e.stopPropagation()}>
      {act.note && <span>{act.note} </span>}
      <Link to={act.href} className="font-medium text-accent hover:underline">
        {act.label}
      </Link>
    </p>
  );
}

/** The newest failed run's text is a partial failure (amber): the row's dot follows it. */
function usePartialFailure(automation: AutomationStatus, enabled: boolean): boolean {
  const { data } = useAutomationHistory(enabled ? automation.id : null);
  if (!data) return false;
  const latest = orderedRuns(data.runs, automation).find(isFailedRun);
  return !!latest && partialFailure(failedRunText(latest)) !== null;
}

/** When a history row is shown at: a never-started run at its scheduled slot, a cut-short one at its start. */
function shownAtFor(automation: Pick<AutomationStatus, 'schedule' | 'scheduleHuman'>) {
  return (run: AutomationHistoryRun) =>
    runShownAt(run, isMissedRun(run) ? restartCasualtyKind(run.error) : null, (at) =>
      scheduledSlot(automation.schedule, automation.scheduleHuman, at),
    );
}

/** The run history newest first by the time each row shows (the API sorts by start). */
function orderedRuns(runs: AutomationHistoryRun[], automation: Pick<AutomationStatus, 'schedule' | 'scheduleHuman'>) {
  return sortByShownTime(runs, shownAtFor(automation));
}

/**
 * The history as the modal lists it: one row per schedule slot for runs a
 * restart lost. A slot that completed after all (a re-run) drops its missed and
 * interrupted rows; one that did not keeps a single restart row.
 */
function historyRows(runs: AutomationHistoryRun[], automation: Pick<AutomationStatus, 'schedule' | 'scheduleHuman'>) {
  return oneRowPerSlot(
    runs,
    (r) => slotOfRun(automation.schedule, automation.scheduleHuman, new Date(r.startedOn ?? r.createdOn))?.toISOString() ?? null,
    (r) => (isMissedRun(r) ? restartCasualtyKind(r.error) : null),
    (r) => r.state === 'completed',
  );
}

/**
 * The current unbroken run of failures, newest first: when it began and how
 * many runs it spans. Null when the latest run did not fail. `atLeast` when the
 * streak reaches back past the history we can read.
 */
function failingStreak(runs: AutomationHistoryRun[]): { since: string; count: number; atLeast: boolean } | null {
  // Unfinished runs (queued, running) and missed runs (lost to a restart) do not break or extend a streak.
  const finished = runs.filter((r) => !isMissedRun(r) && (r.completedOn !== null || isFailedRun(r)));
  if (finished.length === 0 || !isFailedRun(finished[0]!)) return null;
  let count = 0;
  while (count < finished.length && isFailedRun(finished[count]!)) count++;
  const first = finished[count - 1]!;
  return { since: first.startedOn ?? first.createdOn, count, atLeast: count === finished.length && runs.length >= HISTORY_LIMIT };
}

/** "failing since 31 Aug · 7 runs". */
function streakLabel(streak: { since: string; count: number; atLeast: boolean }): string {
  return `failing since ${streak.atLeast ? 'at least ' : ''}${formatDayMonth(streak.since)} · ${streak.atLeast ? `${streak.count}+` : streak.count} run${streak.count === 1 ? '' : 's'}`;
}

/* ─── Automation Detail Modal ─── */

function AutomationDetailModal({
  automation,
  onClose,
  deployedAt = null,
}: {
  automation: AutomationStatus;
  onClose: () => void;
  deployedAt?: string | null;
}) {
  const { data, isLoading } = useAutomationHistory(automation.id);
  const { mutate: stopAutomationMutation, isPending: isStopping } = useStopAutomation();

  // A run left unfinished past the stuck limit is not running: its process died.
  const hasActiveRun = data?.runs.some((r) =>
    (r.state === 'active'
    || r.state === 'created'
    || r.state === 'pending'
    || r.state === 'running')
    && !isStuckRun(r)
  ) ?? false;

  const statusConfig: Record<DisplayStatus, { label: string; badge: 'success' | 'error' | 'info' | 'warning' | 'default' }> = {
    idle: { label: 'Idle: not connected', badge: 'default' },
    healthy: { label: 'Healthy', badge: 'success' },
    failed: { label: 'Failed', badge: 'error' },
    running: { label: 'Running', badge: 'default' },
    stuck: { label: 'Stuck', badge: 'warning' },
    unknown: { label: 'Never ran', badge: 'default' },
    skipped: { label: 'Skipped', badge: 'default' },
    paused: { label: 'Paused', badge: 'default' },
    missed: { label: 'Missed its slot', badge: 'warning' },
  };

  // The row and the dialog name a stale failure the same way.
  // The header says what the latest failed run says: a failure that left most of the work done is "Partly failed".
  const latestFailed = automation.status === 'failed' ? orderedRuns(data?.runs ?? [], automation).find(isFailedRun) : undefined;
  const partialLatest = !!latestFailed && partialFailure(failedRunText(latestFailed)) !== null;
  const s = automation.status === 'unknown' && hasActiveRun
    ? statusConfig['running']
    : isStaleFailure(automation)
    ? { label: 'Stale failure', badge: 'error' as const }
    : partialLatest
    ? { label: 'Partly failed', badge: 'warning' as const }
    : statusConfig[displayStatus(automation)];
  const miss = displayStatus(automation) === 'missed' ? slotMissWithHistory(slotMiss(automation), data?.runs, automation) : null;
  // A run that started in the slot and was cancelled is "Cancelled", the same word the list and Home use.
  const pill = miss?.reason === 'cancelled' && s === statusConfig.missed ? { label: 'Cancelled', badge: 'warning' as const } : s;
  const act = automation.status === 'failed' ? ACT_LINKS[automation.id] : undefined;

  const shownAt = useMemo(() => shownAtFor(automation), [automation]);
  const ordered = useMemo(() => orderedRuns(data?.runs ?? [], automation), [data?.runs, automation]);
  const runs = useMemo(
    // Back-to-back runs that failed the same way, or skipped for the same reason, fold into one row.
    () =>
      groupRepeatedFailures(historyRows(ordered, automation), (r) =>
        isFailedRun(r) ? `failed:${failureHeadline(r)}` : runSkipReason(r) ? `skipped:${runSkipReason(r)}` : null,
      ),
    [ordered, automation],
  );
  // A daily job's history notes the days it did not run at all.
  const dailyGaps = firesOncePerDay(automation.schedule);
  const totalRuns = data?.runs.length ?? 0;
  const failedRuns = (data?.runs ?? []).filter(isFailedRun).length;
  const streak = failingStreak(ordered);
  // The same "Needs a fix: next run Mon 06:00" line the list row states, from the same history.
  const nextAt = nextRun(automation.schedule, automation.scheduleHuman, new Date());
  const fixNote =
    automation.status === 'failed' && !isStaleFailure(automation) && latestFailed && nextAt
      ? nextRunNote(
          latestFailed.completedOn ?? latestFailed.startedOn ?? latestFailed.createdOn,
          isConfigGap(failedRunText(latestFailed)),
          deployedAt,
          nextRunLabel(nextAt, new Date()),
          streak?.count ?? 1,
        )
      : null;
  const firstFailedGroup = runs.findIndex((g) => isFailedRun(g.run));

  return (
    <Modal isOpen={true} onClose={onClose} size="lg">
      {/* Own header instead of the Modal title, so it stays pinned (with its close button) on long histories. */}
      <div className="sticky top-0 z-10 -mx-6 -mt-4 mb-4 rounded-t-2xl border-b border-border-light bg-surface-primary px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <DialogTitle as="h3" className="text-heading-md text-text-primary">
            {automation.name}
          </DialogTitle>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-1.5 text-text-muted transition-colors hover:bg-surface-secondary hover:text-text-secondary"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-body-sm text-text-tertiary">{automation.description}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge variant={pill.badge}>{pill.label}</Badge>
          {automation.service !== 'api' && (
            <span className="text-body-xs text-text-muted">
              {SERVICE_LABELS[automation.service] ?? automation.service}
            </span>
          )}
          <span className="text-body-xs text-text-tertiary">
            {scheduleLabel(automation.scheduleHuman)}
            {/* Only a clock schedule has a timezone; a webhook trigger does not */}
            {automation.schedule.trim().split(/\s+/).length === 5 && ' · Brussels time'}
          </span>
        </div>
        {miss && <p className="mt-2 text-body-xs text-warning-dark">{slotMissLine(miss)}. {slotMissNote(miss)}</p>}
        {isStaleFailure(automation) && (
          <p className="mt-2 text-body-xs text-text-secondary">
            Needs a decision: fix the cause and use the play button on its row to confirm, or retire it (a code change: the OS has no pause switch).
          </p>
        )}
        {fixNote && <p className="mt-2 text-body-xs text-text-secondary">{fixNote}</p>}
        {act && <ActLink act={act} />}
        {displayStatus(automation) === 'idle' && (
          <p className="mt-2 text-body-xs text-text-secondary">
            {automation.idleReason ? `${capitalize(automation.idleReason)}. ` : ''}
            <Link to="/integrations" className="font-medium text-accent hover:underline">
              Open Integrations
            </Link>{' '}
            to connect it.
          </p>
        )}
      </div>

      <div className="space-y-1">
        <div className="mb-3 flex items-center gap-2">
          <h4 className="text-body-sm font-medium text-text-primary">Run history</h4>
          {/* The same streak the row states, from the same 100-run query */}
          {streak ? (
            <span className="text-body-xs text-error-dark">{streakLabel(streak).replace(/^f/, 'F')}</span>
          ) : failedRuns > 0 ? (
            <span className="text-body-xs text-text-tertiary">
              Failed {failedRuns} of the last {totalRuns} run{totalRuns === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>

        {isLoading ? (
          // Reserves the list's height so the dialog does not jump when the runs land.
          <LoadingState height={480} label="Loading run history…" />
        ) : data?.historySource === 'none' ? (
          <div className="py-8 text-center">
            <p className="text-body-sm text-text-tertiary">
              Run history is not available for this external automation.
            </p>
            <p className="text-body-xs text-text-muted mt-1">
              This job runs outside of the API scheduler and is not tracked in pg-boss.
            </p>
          </div>
        ) : !data?.runs.length ? (
          <div className="py-8 text-center">
            <p className="text-body-sm text-text-tertiary">No runs recorded yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {runs.map((group, i) => {
              const oldest = group.repeats[group.repeats.length - 1] ?? group.run;
              const older = runs[i + 1]?.run;
              const gap = dailyGaps && older ? noRunNote(shownAt(oldest), shownAt(older)) : null;
              return (
                <div key={`${group.run.createdOn}-${i}`}>
                  <AutomationRunHistoryRow
                    run={group.run}
                    shownAt={shownAt(group.run)}
                    shownAtOf={shownAt}
                    repeats={group.repeats}
                    showFailedEyebrow={i === firstFailedGroup}
                    onStop={() => stopAutomationMutation(automation.id)}
                    isStopping={isStopping}
                  />
                  {gap && <p className="border-t border-border pb-2 pl-5 pt-2 text-body-xs text-text-muted">{gap}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Why a completed run did nothing: the skip its steps recorded ("no enabled integration"). */
function runSkipReason(run: AutomationHistoryRun): string | null {
  if (run.state !== 'completed' && run.state !== 'skipped') return null;
  const steps = (run.output as { stepResults?: Record<string, unknown> } | null)?.stepResults;
  if (!steps || typeof steps !== 'object') return null;
  for (const v of Object.values(steps)) {
    const skipped = v && typeof v === 'object' ? (v as Record<string, unknown>).skipped : null;
    if (typeof skipped === 'string' && skipped.trim()) return skipped.trim();
  }
  return null;
}

function isFailedRun(run: AutomationHistoryRun): boolean {
  return (run.state === 'failed' || run.state === 'expired') && !isMissedRun(run);
}

/** A run the OS lost to a restart (queue miss, process death): shown amber as "Missed: OS restarted", never a failure. */
function isMissedRun(run: AutomationHistoryRun): boolean {
  return run.state === 'failed' && isMissedRunError(run.error);
}

/** The readable error of a failed run, as it is shown in the history. */
function failedRunText(run: AutomationHistoryRun): string {
  const raw = (run.error ? unescapeNewlines(run.error) : null) ?? extractErrorMessage(run.output) ?? 'Failed with an unknown error';
  return stripDevPaths(raw);
}

/**
 * What a failed run failed on, without the counts that change every run: the
 * failure lines of a Slack-style report ("SEQ-1337 — Linear GraphQL error…"),
 * else the error's first line. Runs that differ only in "Updated: 0 / 1" share it.
 */
function failureHeadline(run: AutomationHistoryRun): string {
  const text = failedRunText(run);
  const report = readSlackReport(text);
  const specific = report.failures.filter((l) => !/^failed:?\s*\d+$/i.test(l));
  if (specific.length > 0) return specific.join(' · ');
  return text.split('\n').map((l) => l.trim()).find(Boolean) ?? 'Failed with an unknown error';
}

/** Lines of a run report that signal a failure. */
const FAILURE_LINE = /❌|\berror\b|\bfailed\b|not found/i;
const EMOJI = /(?:\p{Extended_Pictographic}|\p{Regional_Indicator})(?:\uFE0F|\u200D(?:\p{Extended_Pictographic}))*/gu;

/**
 * A Slack-formatted run report (the same text a job posts to Slack) read as plain
 * text: `*bold*` markers, emoji and :shortcodes: stripped. The lines that signal a
 * failure are pulled out so they can lead; the full report stays available.
 */
function readSlackReport(text: string): { isReport: boolean; failures: string[]; clean: string } {
  const isReport = /\*[^*\n]+\*/.test(text) || /\p{Extended_Pictographic}/u.test(text) || /(^|\s):[a-z0-9_+-]+:(\s|$)/m.test(text);
  const cleanLine = (line: string) =>
    line
      .replace(/\*([^*\n]+)\*/g, '$1')
      .replace(EMOJI, '')
      .replace(/(^|\s):[a-z0-9_+-]+:(?=\s|$)/g, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .trim();
  const lines = text.split('\n');
  const failures = lines.filter((line) => FAILURE_LINE.test(line)).map(cleanLine).filter(Boolean);
  const clean = lines.map(cleanLine).join('\n').replace(/\n{3,}/g, '\n\n').trim();
  return { isReport, failures, clean };
}

/** A failed run's message: for a Slack-style report, what failed first, the full report folded below. */
function FailureMessage({ text, showEyebrow = true }: { text: string; showEyebrow?: boolean }) {
  const report = readSlackReport(text);
  if (!report.isReport) {
    const cause = failureCause(text);
    return (
      <div className="mt-0.5 space-y-1">
        <p className="break-words text-body-xs leading-relaxed text-text-primary [overflow-wrap:anywhere]">{cause}</p>
        {cause !== text.trim() && (
          <details className="min-w-0">
            <summary className="cursor-pointer select-none text-body-xs text-text-tertiary hover:text-text-primary">
              Show details
            </summary>
            <p className="mt-1 whitespace-pre-wrap break-words text-body-xs leading-relaxed text-text-secondary [overflow-wrap:anywhere]">
              {text}
            </p>
          </details>
        )}
      </div>
    );
  }
  return (
    <div className="mt-1 space-y-1.5">
      {report.failures.length > 0 && (
        <div>
          {showEyebrow && <p className="eyebrow text-text-tertiary">What failed</p>}
          <ul className="mt-0.5 space-y-0.5">
            {report.failures.map((line, i) => (
              <li key={i} className="break-words text-body-xs leading-relaxed text-text-primary [overflow-wrap:anywhere]" title={line}>
                {failureCause(line)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <details className="min-w-0" open={report.failures.length === 0}>
        <summary className="cursor-pointer select-none text-body-xs text-text-tertiary hover:text-text-primary">
          Full report
        </summary>
        <p className="mt-1 whitespace-pre-wrap break-words text-body-xs leading-relaxed text-text-secondary [overflow-wrap:anywhere]">
          {report.clean}
        </p>
      </details>
    </div>
  );
}

/** Turns literal backslash-n sequences from serialised errors into real line breaks. */
function unescapeNewlines(text: string): string {
  return text.replace(/\\r\\n|\\n/g, '\n').replace(/\\t/g, '  ');
}

/**
 * A readable message from a failed run's output: the top-level error or message
 * first, then the error / report / message of every step that carried one.
 * Returns null when nothing readable is there; the raw output stays reachable
 * through "Show full output".
 */
function extractErrorMessage(output: unknown): string | null {
  if (!output || typeof output !== 'object') {
    return typeof output === 'string' && output.trim() ? unescapeNewlines(output) : null;
  }
  const obj = output as Record<string, unknown>;
  for (const key of ['error', 'message'] as const) {
    const val = obj[key];
    if (typeof val === 'string' && val.trim()) return unescapeNewlines(val);
    if (val && typeof val === 'object' && typeof (val as Record<string, unknown>)['message'] === 'string') {
      return unescapeNewlines((val as Record<string, unknown>)['message'] as string);
    }
  }
  const stepResults = obj['stepResults'];
  if (stepResults && typeof stepResults === 'object') {
    const lines: string[] = [];
    for (const [step, val] of Object.entries(stepResults as Record<string, unknown>)) {
      if (!val || typeof val !== 'object') continue;
      const r = val as Record<string, unknown>;
      const text = [r['error'], r['report'], r['message']].find(
        (v): v is string => typeof v === 'string' && v.trim().length > 0
      );
      if (text) lines.push(`${step}: ${unescapeNewlines(text)}`);
    }
    if (lines.length > 0) return lines.join('\n\n');
  }
  return null;
}

function prettyOutput(output: unknown): string | null {
  if (output === null || output === undefined) return null;
  try {
    return unescapeNewlines(JSON.stringify(output, null, 2));
  } catch {
    return String(output);
  }
}

/* ─── Daily Morning Sync output type guard ─── */

interface DailySyncIntegrationResult {
  integrationId: string;
  provider: string;
  name: string;
  success: boolean;
  entitiesSynced: number;
  error?: string;
}

interface DailySyncOutput {
  results: DailySyncIntegrationResult[];
  summary: { total: number; succeeded: number; failed: number };
}

function isDailySyncOutput(output: unknown): output is DailySyncOutput {
  if (!output || typeof output !== 'object') return false;
  const o = output as Record<string, unknown>;
  return Array.isArray(o['results']) && typeof o['summary'] === 'object';
}

/* ─── Participant Linker output type guard ─── */

interface LinkerCallParticipant {
  email: string;
  personName: string | null;
  personRecordId: string | null;
  type: 'internal' | 'external';
  action: 'linked' | 'already_linked' | 'skipped_no_match' | 'skipped_not_in_attio' | 'error';
  companyName: string | null;
}

interface LinkerCall {
  meetingId: string;
  title: string;
  companyName: string | null;
  companyRecordId: string | null;
  participants: LinkerCallParticipant[];
}

interface ParticipantLinkerOutput {
  _type: 'attio_link_participants';
  summary: string;
  calendarEvents: number;
  callsWithRecordings: number;
  linked: number;
  alreadyLinked: number;
  skippedNoMatch: number;
  errors: number;
  calls: LinkerCall[];
}

function extractLinkerOutput(output: unknown): ParticipantLinkerOutput | null {
  if (!output || typeof output !== 'object') return null;
  const o = output as Record<string, unknown>;
  if (o['_type'] === 'attio_link_participants') return o as unknown as ParticipantLinkerOutput;
  const stepResults = o['stepResults'] as Record<string, unknown> | undefined;
  if (!stepResults || typeof stepResults !== 'object') return null;
  for (const val of Object.values(stepResults)) {
    if (val && typeof val === 'object' && (val as Record<string, unknown>)['_type'] === 'attio_link_participants') {
      return val as unknown as ParticipantLinkerOutput;
    }
  }
  return null;
}

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  linked: { label: 'Linked', color: 'text-success' },
  already_linked: { label: 'Already linked', color: 'text-text-muted' },
  skipped_no_match: { label: 'Skipped', color: 'text-text-muted' },
  skipped_not_in_attio: { label: 'Not in Attio', color: 'text-warning' },
  error: { label: 'Error', color: 'text-error' },
};

function AutomationRunHistoryRow({
  run,
  shownAt,
  shownAtOf,
  repeats = [],
  hideStatus = false,
  showFailedEyebrow = true,
  onStop,
  isStopping,
}: {
  run: AutomationHistoryRun;
  /** The time this row shows (and the list sorts by): see runShownAt. */
  shownAt: string;
  /** The same, for the folded repeats. */
  shownAtOf: (run: AutomationHistoryRun) => string;
  /** Older runs that failed on the same headline, folded into this row. */
  repeats?: AutomationHistoryRun[];
  /** "What failed" is said once, on the first failed run in the list. */
  showFailedEyebrow?: boolean;
  /** The heading already says every run failed, so the per-run pill goes. */
  hideStatus?: boolean;
  onStop: () => void;
  isStopping: boolean;
}) {
  // A run that "completed" by skipping every step (UptimeRobot's poll with no connection) is a skip.
  const skipReason = runSkipReason(run);
  const isCompleted = run.state === 'completed' && !skipReason;
  const isMissed = isMissedRun(run);
  // A restart casualty that never started is "Missed" at its slot; one that had started is "Interrupted".
  const casualty = isMissed ? restartCasualtyKind(run.error) : null;
  const isFailed = (run.state === 'failed' || run.state === 'expired') && !isMissed;
  const isSkipped = run.state === 'skipped' || !!skipReason;
  // Unfinished past the stuck limit: the process running it died (a restart, out of memory).
  const isStuck = isStuckRun(run);
  const isActive =
    !isStuck
    && (run.state === 'active'
    || run.state === 'created'
    || run.state === 'pending'
    || run.state === 'running');
  const isCancelled = run.state === 'cancelled';

  const syncOutput = isDailySyncOutput(run.output) ? run.output : null;
  const linkerOutput = extractLinkerOutput(run.output);
  const hasPartialFailure = syncOutput !== null && syncOutput.summary.failed > 0;
  // Failed on a part while the rest ran (one tender source down): amber, cause first.
  const partial = isFailed ? partialFailure(failedRunText(run)) : null;

  const statusColor = partial
    ? 'bg-warning'
    : isMissed
    ? 'bg-warning'
    : isCompleted
    ? hasPartialFailure ? 'bg-warning' : 'bg-success'
    : isFailed ? 'bg-error'
    : isCancelled || isSkipped ? 'bg-text-muted'
    : 'bg-warning';

  const statusLabel = isStuck
    ? 'Stuck'
    : partial
    ? 'Partly failed'
    : isMissed
    ? casualty === 'interrupted' ? 'Interrupted (OS restarted)' : `Missed ${brusselsHm.format(new Date(shownAt))}`
    : isCompleted
    ? hasPartialFailure ? 'Partial' : 'Completed'
    : isSkipped ? 'Skipped'
    : run.state === 'failed' ? 'Failed'
    : run.state === 'expired' ? 'Expired'
    : run.state === 'active' ? 'Running'
    : run.state === 'created' ? 'Queued'
    : run.state === 'running' ? 'Running'
    : run.state === 'pending' ? 'Queued'
    : run.state === 'cancelled' ? 'Cancelled'
    : run.state === 'skipped' ? 'Skipped'
    : run.state;

  const badgeVariant = isMissed || partial
    ? 'warning' as const
    : isCompleted
    ? hasPartialFailure ? 'warning' as const : 'success' as const
    : isFailed ? 'error' as const
    : isCancelled || isSkipped ? 'default' as const
    : 'warning' as const;

  const duration = formatDuration(run.startedOn ?? run.createdOn, run.completedOn);

  const outputSummary = !syncOutput && !linkerOutput && run.output && typeof run.output === 'object' && 'summary' in run.output && typeof (run.output as Record<string, unknown>).summary === 'string'
    ? (run.output as Record<string, unknown>).summary as string
    : null;

  const stepSummary = runOutputSummary(run.output);

  const explanation = isCompleted
    ? syncOutput
      ? syncOutput.summary.failed === 0
        ? `All ${syncOutput.summary.total} integrations synced${duration ? ` in ${duration}` : ''}`
        : `${syncOutput.summary.succeeded}/${syncOutput.summary.total} integrations synced, ${syncOutput.summary.failed} failed${duration ? ` · ${duration}` : ''}`
      : linkerOutput
        ? `${linkerOutput.callsWithRecordings} calls · ${linkerOutput.linked} linked · ${linkerOutput.alreadyLinked} already linked${linkerOutput.errors > 0 ? ` · ${linkerOutput.errors} errors` : ''}${duration ? ` · ${duration}` : ''}`
        : outputSummary
          ? `${outputSummary}${duration ? ` · ${duration}` : ''}`
          : stepSummary
          ? `${stepSummary}${duration ? ` · ${duration}` : ''}`
          : `Completed, no output recorded${duration ? ` · ${duration}` : ''}`
    : isSkipped
    ? `Skipped: ${skipReason ?? 'nothing to act on'}`
    : isFailed
    ? partial ? `${partial.cause}\n\n${partial.rest}` : failedRunText(run)
    : isStuck
    ? `Started ${brusselsHm.format(new Date(run.startedOn ?? run.createdOn))} and never finished: the process running it stopped (an OS restart or out of memory), so nothing will finish it.`
    : isActive
    ? 'Currently in progress'
    : isCancelled
    ? `${run.startedOn ? `Started ${brusselsHm.format(new Date(run.startedOn))}, cancelled ${brusselsHm.format(new Date(run.completedOn ?? run.createdOn))}` : 'Cancelled'} with Stop or Clear, or through the API. The OS does not record who.`
    : `State: ${run.state}`;

  const fullOutput = isFailed ? prettyOutput(run.output) : null;

  // The time a run ended, as the list's "Last:" shows it; a run still going
  // shows its start, a run a restart lost its slot or start (never the reap time).
  const formattedDate = formatDayMonthYearTime(shownAt);

  return (
    <div className="flex items-start gap-3 py-3">
      <div className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${statusColor} ${isActive ? 'animate-pulse' : ''}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-body-sm font-medium text-text-primary">{formattedDate}</span>
          {run.trigger === 'manual' && <span className="text-body-xs text-text-tertiary">manual</span>}
          {(!hideStatus || isMissed) && <Badge variant={badgeVariant}>{statusLabel}</Badge>}
          {repeats.length > 0 && (
            <span
              className="text-body-xs text-text-tertiary"
              title={repeats.map((r) => formatDayMonthYearTime(shownAtOf(r))).join('\n')}
            >
              {isSkipped
                ? `${repeats.length + 1} skipped polls, nothing to check`
                : `same error ×${repeats.length + 1} · also ${repeatDates(repeats.map((r) => formatDayMonthYearTime(shownAtOf(r))))}`}
            </span>
          )}
          {(isActive || isStuck) && (
            <Button
              size="sm"
              variant="outline"
              onClick={onStop}
              disabled={isStopping}
              isLoading={isStopping}
            >
              {isStuck ? 'Clear' : 'Stop'}
            </Button>
          )}
          {run.retryCount > 0 && (
            <span className="text-body-xs text-text-muted">
              {run.retryCount} {run.retryCount === 1 ? 'retry' : 'retries'}
            </span>
          )}
        </div>
        {isStuck ? (
          <p className="mt-0.5 text-body-xs leading-relaxed text-warning-dark">{explanation}</p>
        ) : isMissed ? (
          <p className="mt-0.5 text-body-xs leading-relaxed text-warning-dark" title={failedRunText(run)}>
            {casualty === 'interrupted'
              ? 'The OS restarted while this run was going. It may have done part of its work; it runs again at the next slot.'
              : /re-queued once/i.test(run.error ?? '')
                ? 'The OS restarted before this run started, so nothing ran. It was re-queued once after the restart.'
                : 'The OS restarted before this run started, so nothing ran. It runs again at the next slot.'}
          </p>
        ) : isFailed ? (
          <FailureMessage text={explanation} showEyebrow={showFailedEyebrow} />
        ) : (
          <p className="mt-0.5 break-words text-body-xs leading-relaxed text-text-tertiary [overflow-wrap:anywhere]">{explanation}</p>
        )}
        {isFailed && fullOutput && (
          <details className="mt-1.5 min-w-0">
            <summary className="cursor-pointer select-none text-body-xs text-text-tertiary hover:text-text-primary">
              Show full output
            </summary>
            <pre className="mt-1.5 max-h-80 max-w-full overflow-auto whitespace-pre-wrap break-words rounded border border-border-light bg-surface-secondary p-2.5 font-mono text-[11px] leading-relaxed text-text-secondary [overflow-wrap:anywhere]">
              {fullOutput}
            </pre>
          </details>
        )}

        {/* Per-integration breakdown */}
        {syncOutput && (
          <div className="mt-2 space-y-1 pl-1">
            {syncOutput.results.map((r) => (
              <div key={r.integrationId} className="flex items-start gap-2">
                <div className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${r.success ? 'bg-success' : 'bg-error'}`} />
                <div className="min-w-0 break-words">
                  <span className="text-body-xs font-medium text-text-secondary">{r.name}</span>
                  {r.success
                    ? <span className="text-body-xs text-text-muted ml-1.5">{r.entitiesSynced} entities</span>
                    : <span className="ml-1.5 line-clamp-2 break-words text-body-xs text-error [overflow-wrap:anywhere]" title={r.error}>{failureCause(r.error)}</span>
                  }
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Participant → Company linker breakdown */}
        {linkerOutput && (
          <div className="mt-2 space-y-2 pl-1">
            {/* Summary */}
            <div className="text-body-xs text-text-muted">
              {linkerOutput.calendarEvents} calendar events &rarr; {linkerOutput.callsWithRecordings} calls with recordings &middot; {linkerOutput.linked} linked &middot; {linkerOutput.alreadyLinked} already linked
            </div>

            {/* Per-call breakdown */}
            {linkerOutput.calls.map((call) => {
              const internal = call.participants.filter((p) => p.type === 'internal');
              const external = call.participants.filter((p) => p.type === 'external');
              return (
                <div key={call.meetingId} className="border-t border-border pt-1.5">
                  <div className="break-words text-body-xs font-medium text-text-secondary">
                    {call.title}
                    {call.companyName && (
                      <span className="font-normal text-text-muted ml-1.5">
                        {call.companyRecordId ? (
                          <a
                            href={`https://app.attio.com/companies/${call.companyRecordId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline decoration-dotted hover:text-text-primary"
                          >
                            {call.companyName}
                          </a>
                        ) : call.companyName}
                      </span>
                    )}
                  </div>
                  <div className="text-body-xs text-text-muted mt-0.5 mb-1">
                    {internal.length} internal &middot; {external.length} external
                  </div>
                  <div className="space-y-0.5">
                    {external.map((p) => {
                      const actionInfo = ACTION_LABELS[p.action] ?? { label: p.action, color: 'text-text-muted' };
                      return (
                        <div key={p.email} className="flex items-start gap-2 text-body-xs">
                          <div className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${p.action === 'linked' ? 'bg-success' : p.action === 'error' ? 'bg-error' : 'bg-text-muted'}`} />
                          <div className="min-w-0 break-words [overflow-wrap:anywhere]">
                            {p.personRecordId ? (
                              <a
                                href={`https://app.attio.com/people/${p.personRecordId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-text-secondary underline decoration-dotted hover:text-text-primary"
                              >
                                {p.personName ?? p.email}
                              </a>
                            ) : (
                              <span className="text-text-secondary">{p.personName ?? p.email}</span>
                            )}
                            {p.personName && <span className="text-text-muted ml-1">({p.email})</span>}
                            <span className={`ml-1.5 ${actionInfo.color}`}>
                              {actionInfo.label}
                              {p.action === 'linked' && p.companyName && ` → ${p.companyName}`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function formatDuration(startedAt: string, completedAt: string | null): string | null {
  if (!completedAt) return null;
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  return `${Math.round(ms / 60_000)}m`;
}
